<script setup lang="ts">
// A multiview input's layout and tiles: pick a preset layout, click a tile
// in the preview and choose what it shows, its label, meters and format
// line. Sources that would show this multiview itself (directly, through
// another multiview, an encode of it or a destination of such an encode)
// are not offered; the node refuses them too.
import { computed, onMounted, ref } from 'vue'
import { api, type Config, type Multiview, type Rect, type Tile } from '../api'
import { rectStyle } from '../util'
import LayoutPicker from './LayoutPicker.vue'

const mv = defineModel<Multiview>({ required: true })
const props = defineProps<{ selfId: string; cfg: Config }>()

const layouts = ref<{ name: string; tiles: Rect[] }[]>([])
onMounted(async () => {
    layouts.value = await api.multiviewLayouts()
    select(0)
})
const rects = computed(() => layouts.value.find((l) => l.name === mv.value.layout)?.tiles ?? [])
const sel = ref<number | null>(null)
// the tile being edited: created (empty) when first selected
function select(i: number) {
    while (mv.value.tiles.length <= i) mv.value.tiles.push({ source: '', vu: 2 })
    sel.value = i
}
const cur = computed(() => (sel.value === null ? undefined : mv.value.tiles[sel.value]))

function setLayout(name: string) {
    const n = layouts.value.find((l) => l.name === name)!.tiles.length
    mv.value.layout = name
    mv.value.tiles = mv.value.tiles.slice(0, n) // the tiles beyond the new layout's go
    if ((mv.value.audio_tile ?? 0) > n) mv.value.audio_tile = 0
    if (sel.value !== null && sel.value >= n) select(0)
}

// does an input's picture depend on the target (this multiview)?
function dependsOn(inputId: string, target: string, seen = new Set<string>()): boolean {
    if (inputId === target) return true
    if (seen.has(inputId)) return false
    seen.add(inputId)
    const inp = props.cfg.inputs.find((i) => i.id === inputId)
    return (inp?.multiview?.tiles ?? []).some((t) => {
        const [k, id] = (t.source ?? '').split(':')
        const encodeOf = (d: string) => props.cfg.destinations.find((x) => x.id === d)?.encode_id
        const via =
            k === 'encode'
                ? props.cfg.encodes.find((e) => e.id === id)?.input_id
                : k === 'destination'
                  ? props.cfg.encodes.find((e) => e.id === encodeOf(id ?? ''))?.input_id
                  : id
        return !!via && dependsOn(via, target, seen)
    })
}
const inputs = computed(() => props.cfg.inputs.filter((i) => !props.selfId || !dependsOn(i.id, props.selfId)))
const encodes = computed(() =>
    props.cfg.encodes.filter((e) => !props.selfId || !e.input_id || !dependsOn(e.input_id, props.selfId)),
)
// a destination's tile: its encode's stream at its delay (what it sends now)
const destinations = computed(() =>
    props.cfg.destinations.filter((d) => !d.id.startsWith('_') && encodes.value.some((e) => e.id === d.encode_id)),
)
const destLabel = (d: { name: string; delay_seconds?: number }) =>
    d.delay_seconds ? `${d.name} (${d.delay_seconds} s behind)` : d.name
function sourceName(s?: string) {
    const [k, id] = (s ?? '').split(':')
    if (k === 'input') return props.cfg.inputs.find((i) => i.id === id)?.name ?? id
    if (k === 'encode') return props.cfg.encodes.find((e) => e.id === id)?.name ?? id
    if (k === 'destination') return props.cfg.destinations.find((d) => d.id === id)?.name ?? id
    return ''
}
const tileText = (i: number) => {
    const t = mv.value.tiles[i]
    return t?.label || sourceName(t?.source) || 'empty'
}
const badges = (i: number) => {
    const t = mv.value.tiles[i]
    if (!t?.source) return ''
    return [
        t.source.startsWith('encode:') ? 'encode' : t.source.startsWith('destination:') ? 'destination' : '',
        t.no_label ? 'no label' : '',
        t.vu ? `VU ${t.vu}` : '',
        t.format ? 'format' : '',
        mv.value.audio_tile === i + 1 ? 'audio' : '',
    ]
        .filter(Boolean)
        .join(' · ')
}
// every tile of the layout at once (Niels): labels shown or hidden, meters
// on or off. Meters on gives a tile without any a stereo pair and leaves
// one set to 8 or 16 as it is.
function allTiles(f: (t: Tile) => void) {
    for (let i = 0; i < rects.value.length; i++) {
        while (mv.value.tiles.length <= i) mv.value.tiles.push({ source: '', vu: 2 })
        f(mv.value.tiles[i]!)
    }
}
const allLabels = (on: boolean) => allTiles((t) => (t.no_label = on ? undefined : true))
const allMeters = (on: boolean) => allTiles((t) => (t.vu = on ? t.vu || 2 : 0))
const labelsOn = computed(() => mv.value.tiles.some((t) => t.source && !t.no_label))
const metersOn = computed(() => mv.value.tiles.some((t) => t.source && t.vu))
</script>

<template>
    <div class="mb-2.5">
        <label>Layout</label>
        <LayoutPicker :layout="mv.layout" :layouts="layouts" @pick="setLayout" />
    </div>
    <div class="mb-1 flex flex-wrap items-center gap-1.5 text-xs" data-test="mv-all">
        <span class="text-fg-2">All tiles:</span>
        <button type="button" class="btn-ghost px-1.5 py-0.5 text-xs" @click="allLabels(!labelsOn)">
            {{ labelsOn ? 'Hide labels' : 'Show labels' }}
        </button>
        <button type="button" class="btn-ghost px-1.5 py-0.5 text-xs" @click="allMeters(!metersOn)">
            {{ metersOn ? 'Hide VU meters' : 'Show VU meters' }}
        </button>
    </div>
    <label>Tiles · click one to choose what it shows</label>
    <div class="relative mb-2.5 aspect-video w-full overflow-hidden rounded border border-line bg-black">
        <button
            v-for="(r, i) in rects"
            :key="i"
            type="button"
            class="absolute flex flex-col items-start overflow-hidden rounded-none border p-1 text-left text-[11px] leading-tight"
            :class="sel === i ? 'z-1 border-accent bg-bg-3' : 'border-line bg-bg-2'"
            :style="rectStyle(r)"
            @click="select(i)"
        >
            <span class="text-fg-3">{{ i + 1 }}</span>
            <span class="w-full truncate" :class="mv.tiles[i]?.source ? 'text-fg' : 'text-fg-3'">{{
                tileText(i)
            }}</span>
            <span class="w-full truncate text-fg-3">{{ badges(i) }}</span>
        </button>
    </div>
    <template v-if="cur && sel !== null && sel < rects.length">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Tile {{ sel + 1 }} shows</label>
                <select v-model="cur.source">
                    <option value="">nothing (empty)</option>
                    <optgroup label="Inputs">
                        <option v-for="i in inputs" :key="i.id" :value="'input:' + i.id">{{ i.name }}</option>
                    </optgroup>
                    <optgroup v-if="encodes.length" label="Encodes (decoded again: ~1 s behind)">
                        <option v-for="e in encodes" :key="e.id" :value="'encode:' + e.id">{{ e.name }}</option>
                    </optgroup>
                    <optgroup v-if="destinations.length" label="Destinations (what they send, at their delay; decoded)">
                        <option v-for="d in destinations" :key="d.id" :value="'destination:' + d.id">
                            {{ destLabel(d) }}
                        </option>
                    </optgroup>
                </select>
                <div v-if="/^(encode|destination):/.test(cur.source ?? '')" class="mt-1 text-xs text-fg-3">
                    Decoded on the node for as long as the multiview runs (one decoder per encode or destination shown,
                    at its largest tile).
                </div>
            </div>
            <div class="mb-2.5">
                <label class="flex items-center gap-1.5"
                    ><input
                        type="checkbox"
                        class="w-auto"
                        :checked="!cur.no_label"
                        data-test="mv-label-on"
                        @change="cur.no_label = ($event.target as HTMLInputElement).checked ? undefined : true"
                    />
                    Label</label
                ><input
                    v-model="cur.label"
                    :disabled="cur.no_label"
                    :placeholder="cur.no_label ? 'no label' : sourceName(cur.source) || 'none'"
                />
            </div>
            <div class="mb-2.5">
                <label>VU meters</label
                ><select v-model.number="cur.vu">
                    <option :value="0">off</option>
                    <option :value="2">2 channels</option>
                    <option :value="8">8 channels</option>
                    <option :value="16">16 channels</option>
                </select>
            </div>
            <div class="mb-2.5 flex items-end">
                <label class="flex items-center gap-1.5"
                    ><input v-model="cur.format" type="checkbox" class="w-auto" /> show the source's format</label
                >
            </div>
        </div>
    </template>
    <div class="mb-2.5">
        <label>Audio of the multiview</label
        ><select v-model.number="mv.audio_tile">
            <option :value="0">silence</option>
            <template v-for="(t, i) in mv.tiles" :key="i">
                <option v-if="t.source" :value="i + 1">tile {{ i + 1 }} · {{ tileText(i) }} (all channels)</option>
            </template>
        </select>
    </div>
    <p class="text-xs text-fg-3">
        Tiles, labels, meters and the audio change live; the resolution and frame rate restart the multiview. A red
        border and NO SIGNAL mark a tile whose source is lost.
    </p>
</template>
